import React, { useEffect, useMemo, useState } from 'react';
import {
    useTabsNavigationStore, useSmenaStore, APP_VIEWS
} from '../../stores';
import { useApiQuery } from '../../api/hooks';
import { getSeasonFromDate } from '../../utils/season';
import Sidebar from './sidebar/Sidebar';
import Topbar from './topbar/Topbar';
import SmenaGamesLoader from '../../component/smena/SmenaGamesLoader';
import ShiftContent from './content/ShiftContent';
import OrgPage from './content/org/OrgPage';
import CampSettingsPage from './content/camp/CampSettingsPage';
import './styles/theme.css';
import './styles/typography.css';
import './styles/layout.css';
import './styles/controls.css';

const SHIFTS_URL = '/api/camps/admin/smena/simple';

// Каркас новой верстки: sidebar + topbar + контент.
// Вся навигационная логика — на существующем useTabsNavigationStore (функционал 1:1).
export default function AppLayout() {
    const currentView = useTabsNavigationStore(state => state.currentView);
    const selectedSeason = useTabsNavigationStore(state => state.selectedSeason);
    const selectedShiftId = useTabsNavigationStore(state => state.selectedShiftId);
    const setSeason = useTabsNavigationStore(state => state.setSeason);
    const setShift = useTabsNavigationStore(state => state.setShift);
    const setShiftWithSeason = useTabsNavigationStore(state => state.setShiftWithSeason);
    const setSmenaId = useSmenaStore(state => state.setSmenaId);

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const closeSidebar = () => setSidebarOpen(false);

    const { data: rawShifts, isLoading, refetch } = useApiQuery(['smena', 'list'], SHIFTS_URL);

    const shifts = useMemo(() => {
        if (!rawShifts) return null;
        return rawShifts.map(s => ({ ...s, season: getSeasonFromDate(s.startDate) }));
    }, [rawShifts]);

    const seasons = useMemo(
        () => (shifts ? [...new Set(shifts.map(s => s.season))].sort() : []),
        [shifts]
    );
    const shiftsInSeason = useMemo(
        () => (selectedSeason ? shifts?.filter(s => s.season === selectedSeason) || [] : []),
        [shifts, selectedSeason]
    );
    const currentShift = shifts?.find(s => s.id === selectedShiftId);

    // Авто-выбор сезона
    useEffect(() => {
        if (seasons.length > 0 && (!selectedSeason || !seasons.includes(selectedSeason))) {
            setSeason(seasons[0]);
        }
    }, [seasons, selectedSeason, setSeason]);

    // Авто-выбор первой смены в сезоне
    useEffect(() => {
        if (shiftsInSeason.length > 0 && !shiftsInSeason.find(s => s.id === selectedShiftId)) {
            setShift(shiftsInSeason[0].id);
        }
    }, [selectedSeason, shiftsInSeason, selectedShiftId, setShift]);

    // Если текущую смену удалили — переключаемся на ближайшую
    useEffect(() => {
        if (selectedShiftId && shifts && !shifts.find(s => s.id === selectedShiftId)) {
            const fallbackShift = shiftsInSeason[0] || shifts[0];
            if (fallbackShift) setShiftWithSeason(fallbackShift.id, fallbackShift.season);
            else setShift(null);
        }
    }, [selectedShiftId, shifts, shiftsInSeason, setShift, setShiftWithSeason]);

    // Синхронизация smenaId для игр
    useEffect(() => {
        if (selectedShiftId) setSmenaId(selectedShiftId);
    }, [selectedShiftId, setSmenaId]);

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                Загрузка...
            </div>
        );
    }

    return (
        <div className={`app-shell${sidebarOpen ? ' sidebar-open' : ''}`}>
            <SmenaGamesLoader />
            <Sidebar onNavigate={closeSidebar} />
            <div className="sidebar-overlay" onClick={closeSidebar} />

            <div className="main">
                <Topbar
                    seasons={seasons}
                    shifts={shiftsInSeason}
                    refetchShifts={refetch}
                    onBurgerClick={() => setSidebarOpen(true)}
                />
                <div className="wrap">
                    {renderContent(currentView, currentShift, refetch)}
                </div>
            </div>
        </div>
    );
}

function renderContent(currentView, currentShift, refetch) {
    if (currentView === APP_VIEWS.ORGANIZERS) return <OrgPage />;
    if (currentView === APP_VIEWS.CAMP_SETTINGS) return <CampSettingsPage />;

    if (!currentShift) {
        return <div style={{ textAlign: 'center', padding: '64px 0' }}>Выберите смену</div>;
    }

    return <ShiftContent shift={currentShift} refetchShifts={refetch} />;
}
